/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
// import WelcomeBanner from './WelcomeBanner'

function Dashboard({ session, profile, onNavigate, onOpenAtRiskPlan, openAllActions }) {
  // const [showBanner, setShowBanner] = useState(!profile?.onboarding_dismissed)
  const [bidHealthSummary, setBidHealthSummary] = useState(null)
  const orgId = profile?.organisation_id
  const [atRiskCount, setAtRiskCount] = useState(0)
  const [atRiskOpps, setAtRiskOpps] = useState([])
  const [nextDeadline, setNextDeadline] = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [newOppCount, setNewOppCount] = useState(0)
  const [winRate, setWinRate] = useState(null)

  useEffect(() => {
    if (orgId) {
      loadAtRiskData()
      loadNextDeadline()
      loadActions()
      loadNewOppCount()
      loadWinRate()
      loadBidHealth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [orgId])

  const loadAtRiskData = async () => {
    const { data: orgPlans } = await supabase
      .from('bid_plans')
      .select('id, opportunity_id, title, submission_deadline, status')
      .eq('organisation_id', orgId)

    const orgPlanIds = orgPlans?.map(p => p.id) || []

    if (orgPlanIds.length === 0) {
      setAtRiskCount(0)
      setAtRiskOpps([])
      return
    }

    const { data: milestones } = await supabase
      .from('milestones')
      .select('id, bid_plan_id, status, due_date')
      .in('bid_plan_id', orgPlanIds)

    const now = new Date()
    const atRiskMilestones = (milestones || []).filter(m => {
      if (m.status === 'at_risk' || m.status === 'missed') return true
      if (m.status === 'complete') return false
      if (m.status === 'pending' && m.due_date && new Date(m.due_date) < now) return true
      return false
    })

    if (atRiskMilestones.length === 0) {
      setAtRiskCount(0)
      setAtRiskOpps([])
      return
    }

    setAtRiskCount(atRiskMilestones.length)

    const planIds = [...new Set(atRiskMilestones.map(m => m.bid_plan_id))]
    const plans = orgPlans.filter(p => planIds.includes(p.id))
    const oppIds = [...new Set(plans.map(p => p.opportunity_id).filter(Boolean))]

    if (oppIds.length > 0) {
      const { data: opps } = await supabase
        .from('opportunities')
        .select('id, title, buyer')
        .in('id', oppIds)

      const oppPlanMap = {}
      plans.forEach(plan => {
        if (!plan.opportunity_id) return
        if (!oppPlanMap[plan.opportunity_id]) oppPlanMap[plan.opportunity_id] = plan
      })

      const enriched = (opps || []).map(opp => ({
        ...opp,
        plan: oppPlanMap[opp.id],
        milestoneCount: atRiskMilestones.filter(m =>
          plans.find(p => p.id === m.bid_plan_id && p.opportunity_id === opp.id)
        ).length
      }))

      setAtRiskOpps(enriched)
    }

    const plansWithNoOpp = plans.filter(p => !p.opportunity_id)
    if (plansWithNoOpp.length > 0) {
      setAtRiskOpps(prev => [
        ...prev,
        ...plansWithNoOpp.map(p => ({
          id: p.id,
          title: p.title,
          buyer: 'No linked opportunity',
          plan: p,
          milestoneCount: atRiskMilestones.filter(m => m.bid_plan_id === p.id).length
        }))
      ])
    }
  }

  const loadBidHealth = async () => {
    const { data } = await supabase
      .from('opportunities')
      .select('id, title, bid_health')
      .eq('organisation_id', orgId)
      .in('stage', ['pursuing', 'bid_planning', 'submitted'])
      .not('bid_health', 'is', null)
    if (!data) return
    const critical = data.filter(o => o.bid_health?.status === 'Critical')
    const atRisk = data.filter(o => o.bid_health?.status === 'At Risk')
    setBidHealthSummary({ critical: critical.length, atRisk: atRisk.length, total: data.length })
  }

  const loadNextDeadline = async () => {
    const { data: upcomingPlans } = await supabase
      .from('bid_plans')
      .select('id, title, submission_deadline')
      .eq('organisation_id', orgId)
      .gte('submission_deadline', new Date().toISOString())
      .order('submission_deadline', { ascending: true })

    if (upcomingPlans && upcomingPlans.length > 0) {
      const earliestDate = upcomingPlans[0].submission_deadline
      const earliestDateDay = new Date(earliestDate).toDateString()
      const sameDayPlans = upcomingPlans.filter(p =>
        new Date(p.submission_deadline).toDateString() === earliestDateDay
      )
      setNextDeadline({ date: earliestDate, plans: sameDayPlans, count: sameDayPlans.length })
    } else {
      setNextDeadline(null)
    }
  }

  const loadActions = async () => {
    const { data } = await supabase
      .from('actions')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('organisation_id', orgId)
      .eq('read', false)
    setUnreadCount(data?.length || 0)
  }

  const markActionsRead = async () => {
    await supabase
      .from('actions')
      .update({ read: true })
      .eq('user_id', session.user.id)
      .eq('organisation_id', orgId)
      .eq('read', false)
    setUnreadCount(0)
  }

  const loadNewOppCount = async () => {
    const startOfMonth = new Date()
    startOfMonth.setDate(1)
    startOfMonth.setHours(0, 0, 0, 0)
    const { count } = await supabase
      .from('opportunities')
      .select('id', { count: 'exact', head: true })
      .eq('organisation_id', orgId)
      .gte('created_at', startOfMonth.toISOString())
    setNewOppCount(count || 0)
  }

  const loadWinRate = async () => {
    const { data } = await supabase
      .from('opportunities')
      .select('stage')
      .eq('organisation_id', orgId)
      .in('stage', ['won', 'lost'])
    if (!data || data.length === 0) { setWinRate(null); return }
    const won = data.filter(o => o.stage === 'won').length
    const total = data.length
    const rate = Math.round((won / total) * 100)
    setWinRate({ won, lost: total - won, total, rate })
  }

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })

  const Tile = ({ className, onClick, front, back, inactive }) => (
    <div
      className={`tile-flip-wrap ${className || ''}`}
      onClick={inactive ? undefined : onClick}
      style={{ cursor: inactive ? 'default' : 'pointer' }}
    >
      <div className="tile-flip-inner">
        <div className="tile-flip-front">{front}</div>
        <div className="tile-flip-back">{back}</div>
      </div>
    </div>
  )

  return (
    <div className="dashboard container">
      {/* {showBanner && (
        <WelcomeBanner
          profile={profile}
          onNavigate={onNavigate}
          onDismiss={() => setShowBanner(false)}
        />
      )} */}
      <div className="dashboard__greeting">
        {greeting()}{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : profile?.username ? `, ${profile.username}` : ''}.
      </div>
      <div className="dashboard__sub">{today} — Your bid workspace is ready.</div>

      <div className="tiles-grid">

        <Tile
          className="tile--wide tile--green-dark"
          onClick={() => onNavigate('opportunities')}
          front={<><div className="tile__label">Opportunity Pipeline</div><div className="tile__value">→</div><div className="tile__title">View and manage all opportunities</div><div className="tile__watermark">£</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Opportunity Pipeline</div><div className="tile-back__desc">Track every opportunity from identification through to outcome. Quick-add new opportunities, run qualification scorecards, manage stage and sector, and link through to all bid activity.</div></div>}
        />

        <Tile
          className="tile--green"
          onClick={() => onNavigate('opportunities')}
          front={<><div className="tile__label">Bid Qualification</div><div className="tile__value">→</div><div className="tile__title">Qualify from the pipeline</div><div className="tile__watermark">Q</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Bid Qualification</div><div className="tile-back__desc">Five-dimension weighted scorecard covering customer relationship, competitive position, solution readiness, commercial attractiveness, and bid resource. Produces a Pursue, Conditional, or No Bid recommendation with PWin estimate.</div></div>}
        />

        <Tile
          className="tile--green-mid"
          onClick={() => onNavigate('opportunities')}
          front={<><div className="tile__label">New Opportunities</div><div className="tile__value">{newOppCount}</div><div className="tile__title">Added this month</div><div className="tile__watermark">O</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">New Opportunities</div><div className="tile-back__desc">Count of opportunities added to your pipeline this month. Click to view the full pipeline.</div></div>}
        />

        <Tile
          className={atRiskCount > 0 ? 'tile--red' : 'tile--green'}
          onClick={() => onOpenAtRiskPlan(atRiskOpps)}
          front={<><div className="tile__label">Milestones At Risk / Missed</div><div className="tile__value">{atRiskCount}</div><div className="tile__title">{atRiskCount === 0 ? 'All milestones on track' : `Across ${atRiskOpps.length} bid${atRiskOpps.length !== 1 ? 's' : ''}`}</div><div className="tile__watermark">!</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Milestones At Risk / Missed</div><div className="tile-back__desc">Shows the total count of milestones marked At Risk or Missed, plus any milestones where the due date has passed without being completed. Click to see the affected bids and go directly to their Gantt plans.</div></div>}
        />

        <Tile
          className="tile--green-dark"
          onClick={() => onNavigate('bidplan')}
          front={<><div className="tile__label">Bid Plan</div><div className="tile__value">→</div><div className="tile__title">Create &amp; manage bid plans</div><div className="tile__watermark">B</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Bid Plan</div><div className="tile-back__desc">Generate structured bid plans with automatic milestone scheduling. Five configurable workstreams, backwards date calculation from submission deadline, scrollable Gantt chart, and individual milestone date editing.</div></div>}
        />

        <Tile
          className="tile--green"
          onClick={() => onNavigate('artefacts')}
          front={<><div className="tile__label">Bid Artefacts</div><div className="tile__value">→</div><div className="tile__title">Kick-off, reviews &amp; sign-off</div><div className="tile__watermark">A</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Bid Artefacts</div><div className="tile-back__desc">Six structured artefact types — Kick-off Pack, Question Storyboard, Blue Team, Black Hat, Red Team, and Sign-Off Pack. Each exports as a branded PowerPoint at the click of a button.</div></div>}
        />

        <Tile
  className={unreadCount > 0 ? 'tile--amber' : 'tile--green'}
  onClick={openAllActions}
  front={
    <>
      <div className="tile__label">Actions</div>
      <div className="tile__value">{unreadCount > 0 ? unreadCount : '✓'}</div>
      <div className="tile__title">
        {unreadCount > 0
          ? `${unreadCount} unread assignment${unreadCount !== 1 ? 's' : ''}`
          : 'No new actions'}
      </div>
      <div className="tile__watermark">A</div>
    </>
  }
  back={
    <div className="tile-back-content">
      <div className="tile-back__title">Actions</div>
      <div className="tile-back__desc">
        Shows unread bid and action assignments.
      </div>
    </div>
  }
/>
        <Tile
          className={winRate ? (winRate.rate >= 50 ? 'tile--green' : winRate.rate >= 25 ? 'tile--amber' : 'tile--red') : 'tile--gold'}
          onClick={() => onNavigate('opportunities', 'won_lost')}
          front={<><div className="tile__label">Win Rate</div><div className="tile__value">{winRate ? `${winRate.rate}%` : '—'}</div><div className="tile__title">{winRate ? `${winRate.won} won · ${winRate.lost} lost · ${winRate.total} decided` : 'No decided bids yet'}</div><div className="tile__watermark">W</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Win Rate</div><div className="tile-back__desc">Percentage of decided bids marked as Won. Only counts opportunities marked Won or Lost. Green above 50%, amber 25–49%, red below 25%.</div></div>}
        />

        <Tile
          className="tile--slate"
          onClick={() => onNavigate('oppintel')}
          front={<><div className="tile__label">Opportunity Intelligence</div><div className="tile__title">Search &amp; Pre-qualify</div><div className="tile__desc">Search Contracts Finder for live notices and AI pre-qualify directly from the results.</div><div className="tile__watermark">I</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Opportunity Intelligence</div><div className="tile-back__desc">Search Contracts Finder for live UK procurement notices, filter by region, value and buyer type, and use AI to pre-qualify results before adding to your pipeline.</div></div>}
        />

        <Tile
          className={bidHealthSummary?.critical > 0 ? 'tile--red' : bidHealthSummary?.atRisk > 0 ? 'tile--amber' : 'tile--green'}
          onClick={() => onNavigate('opportunities')}
          front={<>
            <div className="tile__label">Bid Health</div>
            {bidHealthSummary?.critical > 0 && <div className="tile__value" style={{ color: 'var(--white)' }}>{bidHealthSummary.critical}</div>}
            {bidHealthSummary?.critical > 0 && <div className="tile__title">Critical bid{bidHealthSummary.critical !== 1 ? 's' : ''} need attention</div>}
            {!bidHealthSummary?.critical && bidHealthSummary?.atRisk > 0 && <div className="tile__value" style={{ color: 'var(--white)' }}>{bidHealthSummary.atRisk}</div>}
            {!bidHealthSummary?.critical && bidHealthSummary?.atRisk > 0 && <div className="tile__title">Bid{bidHealthSummary.atRisk !== 1 ? 's' : ''} at risk</div>}
            {(!bidHealthSummary || (bidHealthSummary.critical === 0 && bidHealthSummary.atRisk === 0)) && <div className="tile__title">All bids on track</div>}
            <div className="tile__desc">{bidHealthSummary?.total ? bidHealthSummary.total + ' bid' + (bidHealthSummary.total !== 1 ? 's' : '') + ' assessed' : 'No health assessments yet'}</div>
            <div className="tile__watermark">H</div>
          </>}
          back={<div className="tile-back-content"><div className="tile-back__title">Bid Health</div><div className="tile-back__desc">AI-powered health assessment across all active bids. Flags critical issues, overdue milestones, unassigned actions and deadline risks.</div></div>}
        />
        <Tile
          className="tile--charcoal"
          onClick={() => onNavigate('competitive')}
          front={<><div className="tile__label">Competitive Intelligence</div><div className="tile__title">Competitor Profiles &amp; Ghost Strategy</div><div className="tile__desc">Build competitor profiles, tag to bids, and generate AI ghost strategies.</div><div className="tile__watermark">C</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Competitive Intelligence</div><div className="tile-back__desc">Build and maintain competitor profiles, tag competitors to bids, and generate AI-powered ghost strategies.</div></div>}
        />

        <Tile
          className="tile--slate"
          inactive
          front={<><div className="tile__label">Salesforce</div><div className="tile__title">CRM Integration</div><div className="tile__desc">Bi-directional sync with Salesforce. Opportunities in, outcomes back.</div><div className="tile__coming">Coming — Phase 2</div><div className="tile__watermark">SF</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Salesforce</div><div className="tile-back__desc">Bi-directional sync with Salesforce CRM. Opportunities flow in automatically, bid outcomes and qualification scores flow back.</div><div className="tile-back__coming">Coming in Phase 2</div></div>}
        />

        <Tile
          className="tile--charcoal"
          inactive
          front={<><div className="tile__label">Knowledge Library</div><div className="tile__title">Proof Points &amp; Past Performance</div><div className="tile__desc">Reusable evidence, case studies and CVs with AI-assisted retrieval.</div><div className="tile__coming">Coming — Phase 2</div><div className="tile__watermark">K</div></>}
          back={<div className="tile-back-content"><div className="tile-back__title">Knowledge Library</div><div className="tile-back__desc">Centralised repository of reusable bid content — case studies, proof points, CVs, and accreditations. AI-assisted retrieval surfaces the right evidence for each question automatically.</div><div className="tile-back__coming">Coming in Phase 2</div></div>}
        />

        <Tile
          className="tile--wide tile--green"
          onClick={() => onNavigate('bidplan')}
          front={
            <>
              <div className="tile__label">Next Submission Deadline</div>
              {nextDeadline ? (
                <>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)', marginTop: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {nextDeadline.count === 1 ? nextDeadline.plans[0].title : `${nextDeadline.count} bids due on the same day`}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                    {new Date(nextDeadline.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    {' · '}{Math.ceil((new Date(nextDeadline.date) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                  </div>
                  {nextDeadline.count > 1 && (
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', marginTop: '4px' }}>
                      {nextDeadline.plans.map(p => p.title).join(' · ')}
                    </div>
                  )}
                </>
              ) : (
                <div className="tile__value" style={{ fontSize: '1.5rem', marginTop: '8px' }}>No active bids</div>
              )}
              <div className="tile__watermark">D</div>
            </>
          }
          back={<div className="tile-back-content"><div className="tile-back__title">Next Submission Deadline</div><div className="tile-back__desc">Shows your most imminent submission deadline across all active bids. Handles multiple bids on the same date.</div></div>}
        />

      </div>
    </div>
  )
}

export default Dashboard
